import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowRight, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { resetPassword } from "../../api/auth";
import AuthShell, {
  authInputWithIconClass,
  authPrimaryBtnClass,
} from "./AuthShell";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirm) {
      return setMessage("Mật khẩu nhập lại không khớp.");
    }

    setLoading(true);
    setMessage("");
    try {
      await resetPassword({ token, newPassword });
      setSuccess(true);
      setMessage("Đặt lại mật khẩu thành công!");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setMessage(err.response?.data?.message || "Có lỗi xảy ra.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Bảo mật"
      title="Đặt lại mật khẩu"
      subtitle={
        success
          ? "Bạn sẽ được chuyển về trang đăng nhập."
          : "Tạo mật khẩu mới cho tài khoản của bạn."
      }
      brandDescription="Liên kết đặt lại mật khẩu chỉ dùng một lần. Sau khi đổi xong, hãy đăng nhập bằng mật khẩu mới."
      footer={
        !success && (
          <p className="text-center text-sm text-stone-500">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 font-semibold text-[#8f1219] hover:underline"
            >
              <ArrowLeft size={14} />
              Quay lại đăng nhập
            </Link>
          </p>
        )
      }
    >
      {success ? (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-6 text-center">
          <CheckCircle2 className="mx-auto mb-3 text-emerald-600" size={36} />
          <p className="text-sm font-semibold text-emerald-800">{message}</p>
        </div>
      ) : (
        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-stone-700">Mật khẩu mới</label>
            <div className="relative">
              <Lock
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400"
                size={17}
              />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className={`${authInputWithIconClass} pr-11`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-stone-700">Nhập lại mật khẩu</label>
            <div className="relative">
              <Lock
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400"
                size={17}
              />
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className={`${authInputWithIconClass} pr-11`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                aria-label={showConfirm ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {message && !success && (
            <p className="text-center text-sm font-medium text-red-600">{message}</p>
          )}

          <button type="submit" disabled={loading} className={authPrimaryBtnClass}>
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Đang xử lý...
              </>
            ) : (
              <>
                Đổi mật khẩu
                <ArrowRight size={17} className="transition group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
